class FormHandler {
  static #postalCodeType = "postal_code";
  static #CanadaCountryCode = "CA";
  static #UnitedStateCountryCode = "US";
  static #MexicoCountryCode = "MX";
  onSuccess;
  onError;
  
  constructor(url, formElement, queryParams = null, headers = null, countryCode = null) {
    if (!(formElement instanceof HTMLFormElement)) {
      throw new Error("formElement must be a valid HTMLFormElement");
    }

    this.url = url;
    this.formElement = formElement;
    this.queryParams = queryParams ?? {};
    this.countryCode = countryCode;

    this.headers = new Headers();
    //Public front-end credentials
    this.headers.append(
      "Authorization",
      "Basic TVZobGVsa3FScDk0TlI2NTFseXM6c2ZIUnpVT1FGcEcxclJ6WGx1bE0="
    );
    //Adding the custom headers for this form
    if (headers != null){
      for (const [key, value] of Object.entries(headers)) {
        this.headers.append(key, value);
      }
    }

    this.onSuccess = null;
    this.onError = null;

    //Add `novalidate` to prevent native validation blocking the custom submit logic
    this.formElement.setAttribute("novalidate", "");

    //Saving initial values to be able to reset to them later
    this.initialValues = this._getFormSnapshot();

    this._attachEventListeners();

    if (countryCode == FormHandler.#CanadaCountryCode){
      const fields = this.getAllFormElements();
      fields.filter(field => field.dataset.customType == FormHandler.#postalCodeType).forEach(postalCodeField => {
        postalCodeField.maxLength = 7;
        postalCodeField.addEventListener('input', (e) => {
          const input = e.target;
          const oldPosition = input.selectionStart; // Get cursor position before change
          
          //Remove non-alphanumeric characters
          let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        
          //Add space after the 3rd character if needed
          if (value.length > 2 && value[3] !== ' ') {
            value = value.slice(0, 3) + ' ' + value.slice(3);
          }
        
          const oldLength = e.target.value.length;
          input.value = value;
          const newLength = input.value.length;
          let newPosition = oldPosition;
        
          //Adjust cursor position if space was added
          if (oldLength <= 3 && newLength > 3) newPosition = oldPosition + 1;
        
          //Restore the cursor to the appropriate position
          input.setSelectionRange(newPosition, newPosition);
        });
        postalCodeField.addEventListener('keydown', (e) => {
          const input = e.target;
          
          if (e.key === 'Backspace') {
            const position = input.selectionStart;
        
            // If trying to delete the space, move the cursor before the space
            if (position === 4) {
              e.preventDefault();
              input.setSelectionRange(3, 3);
            }
          }
        });
      });
    }
  }

  _attachEventListeners() {
    this.formElement.addEventListener("submit", (e) => {
      e.preventDefault();
      this.submitForm(e);
    });
  }

  isValid(showErrorsToUser = true) {
    let valid = true;

    if (showErrorsToUser) {
      // Clear existing styles
      this.formElement
        .querySelectorAll("input, select, textarea")
        .forEach((el) => {
          el.classList.remove("invalid-form-value");
        });
    }

    const invalidFields = [];
    const fields = this.getAllFormElements();
    for (const field of fields) {
      if (!this.isFieldValid(field)) {
        valid = false;

        if (showErrorsToUser) {
          let elementToShowError = field;
          
          //If the element is hidden then use the parent to show the error
          elementToShowError = this._getElementToAffect(field);

          invalidFields.push({
            field: field,
            elementToShowError: elementToShowError,
          });

          elementToShowError.classList.add("invalid-form-value");

          const isRadio = field.type === "radio";
          //Add a one time listener to remove the error style
          const eventType = field.tagName === "SELECT" ? "change" : "input";
          
          if (isRadio) {
            const radioGroup = document.querySelectorAll(`input[type="radio"][name="${field.name}"]`);
            radioGroup
            const clearStyle = () => {
              radioGroup.forEach(radio => {
                let radioErrorElement = radio;
                //If the element is hidden then use the parent to show the error
                radioErrorElement = this._getElementToAffect(field);
        
                radioErrorElement.classList.remove("invalid-form-value");
              });
            };
            radioGroup.forEach(radio => {
              radio.addEventListener("change", clearStyle, { once: true });
            });
          }
          else {
            const clearStyle = () => {
              elementToShowError.classList.remove("invalid-form-value");
            };
            elementToShowError.addEventListener(eventType, clearStyle, { once: true });
          }
        }
      }
    }

    const recaptchaV2ResponseField = this.formElement.querySelector(
      "textarea[name='g-recaptcha-response']"
    );
    //If there is a google recaptcha V2 present on the form we want to validate it too (at least check if there is a value, the actual true validation is done in the back-end)
    if (recaptchaV2ResponseField) {
      if (!recaptchaV2ResponseField.value) {
        valid = false;

        if (showErrorsToUser) {
          const elementToShowError = recaptchaV2ResponseField
            .closest(".g-recaptcha")
            .querySelector("div");
          elementToShowError.classList.add("invalid-form-value");

          invalidFields.push({
            field: recaptchaV2ResponseField,
            elementToShowError: elementToShowError,
          });

          //Add a one-time polling function to remove the error style (we have to do it this way because the iframe doesn't trigger any event when changing the textarea)
          let lastValue = recaptchaV2ResponseField.value;

          const intervalId = setInterval(() => {
            if (recaptchaV2ResponseField.value !== lastValue) {
              lastValue = recaptchaV2ResponseField.value;
              elementToShowError.classList.remove("invalid-form-value");
              clearInterval(intervalId);
            }
          }, 200);
        }
      }
    }

    if (!valid && showErrorsToUser) {
      const highestField = invalidFields.reduce((topMost, current) => {
        return current.elementToShowError.getBoundingClientRect().top <
          topMost.elementToShowError.getBoundingClientRect().top
          ? current
          : topMost;
      });

      highestField.elementToShowError.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      highestField.field.focus();
    }

    return valid;
  }

  isFieldValid(field) {
    let isValid = true;
    
    //We check if it's still valid for each check because if it's invalid then we don't need to continue to check the other validation rules

    //If the field is required check if it's empty
    if (isValid && field.required){
      if (field.type === "checkbox") isValid = field.checked;
      else if (field.type === "radio") isValid = Array.from(document.querySelectorAll(`input[type="radio"][name="${field.name}"]`)).some(input => input.checked);
      else isValid = field.value?.trim();
    }
    //If field is an email type check the format
    if (isValid && field.type == "email" && !(new RegExp(/[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/i).test(field.value.trim()))){
      isValid = false;
    }
    //If field is a phone type check the format (loosely checking, the exact valid format is way too complicated)
    else if (isValid && field.type == "tel" && !(new RegExp(/^\+?[0-9\s\-().]{7,}(?:\s?(?:ext|x|extension|#|\*)\s?\d{1,5})?$/i).test(field.value.trim()))){
      isValid = false;
    }
    //If fiels is a postal code type and the form is for canada check the postal code format for canada
    else if (isValid && field.dataset.customType == FormHandler.#postalCodeType && this.country == FormHandler.#CanadaCountryCode && !(new RegExp(/^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/).test(field.value.trim()))){
      isValid = false;
    }
    
    return isValid;
  }

  getAllFormElements(){
    return Array.from(this.formElement.elements).filter(tag => ["select", "textarea", "input"].includes(tag.tagName.toLowerCase()));
  }
  
  getFormData() {
    return new FormData(this.formElement);
  }

  getFullUrl() {
    const query = new URLSearchParams(this.queryParams).toString();
    return query ? `${this.url}?${query}` : this.url;
  }

  async submitForm(event) {
    if (!this.isValid(true)) {
      return;
    }
    //Disable the button while sending to prevent spam
    event.submitter.disabled = true;
    event.submitter.classList.add("form-button-loading");
    
    try {
      const requestOptions = {
        method: "POST",
        headers: this.headers,
        body: this.getFormData(),
        redirect: "follow",
        scheme: "https",
        mode: "cors",
        cache: "no-cache",
      };

      const response = await fetch(this.getFullUrl(), requestOptions);

      if (!response.ok) throw new Error(`HTTP error ${response.status}`);

      const contentType = response.headers.get("Content-Type");
      const isJson = contentType && contentType.includes("application/json");
      const data = isJson ? await response.json() : await response.text();

      if (typeof this.onSuccess === "function") {
        this.onSuccess(data, response);
      }
    } catch (error) {
      if (typeof this.onError === "function") {
        this.onError(error);
      } else {
        console.error("Form submission error:", error);
      }
    } finally{
      //Enable the button after receiving the response or having an error
      event.submitter.disabled = false;
      event.submitter.classList.remove("form-button-loading");
    }
  }

  resetForm() {
    // Reset text inputs, checkboxes, radios, selects, etc. to the initial values snapshotted from the constructor
    for (const [name, value] of Object.entries(this.initialValues)) {
      const elements = this.formElement.elements[name];
      if (!elements) continue;
      if (elements instanceof RadioNodeList) {
        [...elements].forEach((el) => {
          if (el.type === "checkbox" || el.type === "radio") {
            el.checked = value.includes(el.value);
          } else {
            el.value = value[0];
          }
          el.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      else {
        const el = elements;
        if (el.tagName === "SELECT"){
          const optionToSelect = Array.from(el.options).find(opt => opt.value === value[0]);
          if (optionToSelect) optionToSelect.selected = true;
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (el.type === "checkbox" || el.type === "radio") {
          el.checked = value.includes(el.value);
        } else {
          el.value = value[0];
        }
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    //Reset file inputs
    for (const el of this.formElement.querySelectorAll("input[type='file']")) {
      el.value = "";
    }
  }

  //Get a snapshot of the current values in the form. Main use is to be able to reset the form to the initial values later
  _getFormSnapshot() {
    const snapshot = {};
    for (const element of this.formElement.elements) {
      if (!element.name) continue;

      if (element.type === "checkbox" || element.type === "radio") {
        if (!snapshot[element.name]) snapshot[element.name] = [];
        if (element.checked) snapshot[element.name].push(element.value);
      } else {
        snapshot[element.name] = [element.value];
      }
    }
    return snapshot;
  }

  //Use this to affect the actual visible element representing the input (if an input is hidden it will affect the parent instead)
  _getElementToAffect(el){
    //If the element is hidden then use the parent
    if (window.getComputedStyle(el).display === "none") return el.parentElement;
    else return el;
  }
  
  disableInput(el){
    el = this._getElementToAffect(el);
    el.disabled = true;
    el.classList.add("disabled");
  }

  disableAllInputs(){
    this.getAllFormElements().map(el => this._getElementToAffect(el)).forEach(el => {
      el.disabled = true;
      el.classList.add("disabled");
    });
  }

  enableInput(el){
    el = this._getElementToAffect(el);
    el.disabled = false;
    el.classList.remove("disabled");
  }

  enableAllInput(){
    this.getAllFormElements().map(el => this._getElementToAffect(el)).forEach(el => {
      el.disabled = false;
      el.classList.remove("disabled");
    });
  }
}
